import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Typography,
  TextField,
  MenuItem,
  Paper,
  TablePagination,
  IconButton,
  Collapse
} from '@mui/material';
import { Download, Search, Refresh, ExpandMore, ExpandLess } from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosConfig';

interface Transaction {
  _id: string;
  personnelId: {
    _id: string;
    name: string;
    rank?: string;
    email: string;
  };
  action: 'earned' | 'used' | 'adjusted' | 'expired' | 'reverted' | 'notified' | 'rollover' | 'carryover-expired' | 'dismissed';
  leaveType: 'annual-leave' | 'time-lieu';
  amount: number;
  balance: number;
  date: string;
  approvedBy?: {
    _id: string;
    name: string;
    email: string;
  } | string;
    rotaEntryId?: {
    _id: string;
    startDate: string;
    endDate: string;
  } | string;
  description?: string;
  leavePeriodStart?: string;
  leavePeriodEnd?: string;
}

interface Personnel {
  _id: string;
  name: string;
  rank?: string;
}

const AuditLogTab: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [staff, setStaff] = useState<Personnel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  
  // Default filter values
  const getDefaultStartDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 90);
    return date.toISOString().split('T')[0];
  };
  
  const getDefaultEndDate = () => {
    return new Date().toISOString().split('T')[0];
  };
  
  // Filters
  const [selectedStaff, setSelectedStaff] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>(getDefaultStartDate());
  const [endDate, setEndDate] = useState<string>(getDefaultEndDate());
  const [leaveTypeFilter, setLeaveTypeFilter] = useState<string>('all');
  const [actionTypeFilter, setActionTypeFilter] = useState<string>('all');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    fetchStaff();
    fetchTransactions();
  }, []);

  const fetchStaff = async () => {
    try {
      const response = await axiosInstance.get('/api/personnel');
      setStaff(response.data);
    } catch (err) {
      console.error('Failed to fetch staff', err);
    }
  };

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params: any = {
        startDate,
        endDate
      };
      
      if (selectedStaff !== 'all') {
        params.personnelId = selectedStaff;
      }
      if (leaveTypeFilter !== 'all') {
        params.leaveType = leaveTypeFilter;
      }
      if (actionTypeFilter !== 'all') {
        params.action = actionTypeFilter;
      }
      
      const response = await axiosInstance.get('/api/personnel/transactions', { params });
      setTransactions(response.data);
      setPage(0); // Reset to first page on new search
      setLoading(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch transactions');
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchTransactions();
  };

  const handleResetFilters = () => {
    setSelectedStaff('all');
    setStartDate(getDefaultStartDate());
    setEndDate(getDefaultEndDate());
    setLeaveTypeFilter('all');
    setActionTypeFilter('all');
    setPage(0);
  };

  const handleExportCSV = () => {
    const headers = [
      'Transaction Date/Time', 
      'Leave Period', 
      'Staff', 
      'Rank', 
      'Action', 
      'Type', 
      'Amount', 
      'Balance After', 
      'By', 
      'Description'
    ];
    
    const rows = transactions.map(tx => [
      new Date(tx.date).toLocaleString('en-GB'),
      formatLeavePeriod(tx),
      tx.personnelId.name,
      tx.personnelId.rank || '-',
      tx.action,
      tx.leaveType === 'annual-leave' ? 'AL' : 'TIL',
      tx.amount,
      tx.balance,
      typeof tx.approvedBy === 'object' && tx.approvedBy ? tx.approvedBy.name : (tx.approvedBy || '-'),
      tx.description || ''
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leave-audit-log-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'earned': return 'success';
      case 'used': return 'error';
      case 'adjusted': return 'warning';
      case 'reverted': return 'info';
      case 'rollover': return 'info';
      case 'notified': return 'primary';
      case 'carryover-expired': return 'error';
      case 'dismissed': return 'warning';
      case 'expired': return 'default';
      default: return 'default';
    }
  };

  const formatLeavePeriod = (tx: Transaction): string => {
    if (!tx.leavePeriodStart) return '-';
    
    const startDate = new Date(tx.leavePeriodStart);
    const endDate = tx.leavePeriodEnd ? new Date(tx.leavePeriodEnd) : null;
    
    if (!endDate || startDate.toDateString() === endDate.toDateString()) {
      return startDate.toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: '2-digit',
        year: 'numeric'
      });
    }
    
    return `${startDate.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: '2-digit' 
    })} - ${endDate.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: '2-digit',
      year: 'numeric'
    })}`;
  };

  const toggleRowExpansion = (txId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(txId)) {
      newExpanded.delete(txId);
    } else {
      newExpanded.add(txId);
    }
    setExpandedRows(newExpanded);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedTransactions = transactions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading && transactions.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filter Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <TextField
            select
            label="Staff"
            value={selectedStaff}
            onChange={(e) => setSelectedStaff(e.target.value)}
            size="small"
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="all">All Staff</MenuItem>
            {staff.map((person) => (
              <MenuItem key={person._id} value={person._id}>
                {person.name} {person.rank && `(${person.rank})`}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />

          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />

          <TextField
            select
            label="Leave Type"
            value={leaveTypeFilter}
            onChange={(e) => setLeaveTypeFilter(e.target.value)}
            size="small"
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="all">All Types</MenuItem>
            <MenuItem value="annual-leave">AL</MenuItem>
            <MenuItem value="time-lieu">TIL</MenuItem>
          </TextField>

          <TextField
            select
            label="Action"
            value={actionTypeFilter}
            onChange={(e) => setActionTypeFilter(e.target.value)}
            size="small"
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="all">All Actions</MenuItem>
            <MenuItem value="earned">Earned</MenuItem>
            <MenuItem value="used">Used</MenuItem>
            <MenuItem value="adjusted">Adjusted</MenuItem>
            <MenuItem value="reverted">Reverted</MenuItem>
            <MenuItem value="expired">Expired</MenuItem>
            <MenuItem value="notified">Notified (Email)</MenuItem>
            <MenuItem value="rollover">Rollover</MenuItem>
          </TextField>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<Search />}
            onClick={handleSearch}
            disabled={loading}
          >
            Search
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleResetFilters}
          >
            Reset Filters
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExportCSV}
            disabled={transactions.length === 0}
          >
            Export CSV
          </Button>
        </Box>
      </Paper>

      {transactions.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No transactions found
          </Typography>
        </Box>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {transactions.length} {transactions.length === 1 ? 'transaction' : 'transactions'} found
          </Typography>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }}></TableCell>
                  <TableCell>Transaction Date/Time</TableCell>
                  <TableCell>Leave Period</TableCell>
                  <TableCell>Staff</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Balance After</TableCell>
                  <TableCell>By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedTransactions.map((tx) => (
                  <React.Fragment key={tx._id}>
                    <TableRow hover>
                      <TableCell>
                        {tx.description && (
                          <IconButton
                            size="small"
                            onClick={() => toggleRowExpansion(tx._id)}
                          >
                            {expandedRows.has(tx._id) ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(tx.date).toLocaleDateString('en-GB')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(tx.date).toLocaleTimeString('en-GB', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatLeavePeriod(tx)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {tx.personnelId.name}
                        </Typography>
                        {tx.personnelId.rank && (
                          <Typography variant="caption" color="text.secondary">
                            {tx.personnelId.rank}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={tx.action}
                          size="small"
                          color={getActionColor(tx.action)}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={tx.leaveType === 'annual-leave' ? 'AL' : 'TIL'}
                          size="small"
                          color={tx.leaveType === 'annual-leave' ? 'primary' : 'secondary'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {tx.action === 'notified' || tx.amount == null ? (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        ) : (
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            color={
                              tx.action === 'earned' || tx.action === 'reverted' || tx.action === 'rollover'
                                ? 'success.main'
                                : 'error.main'
                            }
                          >
                            {tx.action === 'earned' || tx.action === 'reverted' || tx.action === 'rollover' ? '+' : '-'}
                            {Math.abs(tx.amount)}
                            {tx.leaveType === 'time-lieu' ? 'h' : 'd'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {tx.action === 'notified' || tx.balance == null ? (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        ) : (
                          <Typography variant="body2">
                            {tx.balance}{tx.leaveType === 'time-lieu' ? 'h' : 'd'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {typeof tx.approvedBy === 'object' && tx.approvedBy?.name 
                            ? tx.approvedBy.name 
                            : (typeof tx.approvedBy === 'string' ? tx.approvedBy : '-')
                          }
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {tx.description && (
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                          <Collapse in={expandedRows.has(tx._id)} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 2, pl: 6, pr: 2, bgcolor: 'action.hover' }}>
                              <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                Description:
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {tx.description}
                              </Typography>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={transactions.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </>
      )}
    </Box>
  );
};

export default AuditLogTab;